import React, { useEffect, useMemo, useState } from 'react';
import { apiService } from '../services/apiService';
import { showToast } from './Toast';
import { DSButton, DSInput, DSSelect } from '../design-system/primitives';

// Money settings for a season: the buy-in, how that buy-in divides between the weekly pot and
// the season pool, and the payout structure on each side.
//
// Every one of these has been a column on pingolf_seasons since the table was created, and none
// of them had a way to be set after createSeason - so a $5 card with $1 to the season pool was
// effectively hardcoded. The structures come from the server registry rather than a copy here,
// so the percentages shown are the ones finalization will actually apply.
//
// The division and both payout structures lock once any card has been sold: they are applied at
// finalize off the season row, so changing them mid-season would retroactively redistribute money
// players have already paid in.
//
// The buy-in is the exception and stays editable. A card captures its own price_paid at purchase
// and every pot is summed from those captured amounts, so a new price only reaches cards sold
// after the change - which is exactly what a host raising the buy-in mid-season means.

const formatMoney = (amount) => {
  const value = Number(amount) || 0;
  return `$${value.toLocaleString('en-US', {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2
  })}`;
};

const percent = (share) => `${Math.round(Number(share) * 100)}%`;

const PLACE_LABELS = ['1st', '2nd', '3rd', '4th', '5th', '6th'];

const SEASON_PRIZE_LABELS = {
  champion: 'Champion',
  runner_up: 'Runner-up',
  third: 'Third',
  fourth: 'Fourth'
};

// Season prize order is by place, and object key order is not guaranteed to match it.
const SEASON_PRIZE_ORDER = ['champion', 'runner_up', 'third', 'fourth'];

const orderedSeasonSplit = (split) => SEASON_PRIZE_ORDER
  .filter(key => Number(split?.[key]) > 0)
  .map(key => ({ key, label: SEASON_PRIZE_LABELS[key] || key, share: Number(split[key]) }));

// Which named structure a stored split corresponds to, so the select can show the season's
// current choice. A split set outside this screen may match none of them.
const matchSeasonStructureKey = (split, structures) => {
  const normalize = (obj) => Object.entries(obj || {})
    .filter(([, share]) => Number(share) > 0)
    .map(([prize, share]) => `${prize}:${Number(share).toFixed(4)}`)
    .sort()
    .join('|');

  const target = normalize(split);
  return structures.find(entry => normalize(entry.split) === target)?.key || '';
};

function PinGolfSeasonSettings({ season, cardsSold, onChanged }) {
  const [structures, setStructures] = useState({ weekly: [], season: [] });
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(null);
  const [priceDraft, setPriceDraft] = useState(null);

  useEffect(() => {
    let cancelled = false;
    apiService.getPingolfPayoutStructures()
      .then(res => { if (!cancelled) setStructures(res.data); })
      .catch(() => { if (!cancelled) showToast('Could not load payout structures', 'error'); });
    return () => { cancelled = true; };
  }, []);

  const isLocked = Boolean(season.finalized_at) || cardsSold > 0;

  const cardPrice = Number(season.card_price_dollars) || 0;
  const seasonPerCard = Number(season.season_pool_per_card) || 0;
  const weeklyPerCard = Math.max(cardPrice - seasonPerCard, 0);

  const weeklyStructure = useMemo(
    () => structures.weekly.find(entry => entry.key === season.weekly_payout_preset) || null,
    [structures.weekly, season.weekly_payout_preset]
  );
  const seasonStructureKey = useMemo(
    () => matchSeasonStructureKey(season.season_pool_split, structures.season),
    [season.season_pool_split, structures.season]
  );

  const startEditing = () => {
    setForm({
      cardPriceDollars: String(cardPrice),
      seasonPoolPerCard: String(seasonPerCard),
      weeklyFloorDollars: String(Number(season.weekly_floor_dollars) || 0),
      weeklyPayoutPreset: season.weekly_payout_preset || '',
      seasonPayoutStructure: seasonStructureKey || ''
    });
    setEditing(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      await apiService.updatePingolfSeasonSettings(season.id, {
        cardPriceDollars: Number(form.cardPriceDollars),
        seasonPoolPerCard: Number(form.seasonPoolPerCard),
        weeklyFloorDollars: Number(form.weeklyFloorDollars),
        weeklyPayoutPreset: form.weeklyPayoutPreset,
        seasonPayoutStructure: form.seasonPayoutStructure
      });
      showToast('Season settings saved', 'success');
      setEditing(false);
      onChanged();
    } catch (error) {
      showToast(error.response?.data?.error || 'Failed to save season settings', 'error');
    } finally {
      setBusy(false);
    }
  };

  const savePrice = async () => {
    setBusy(true);
    try {
      const updated = await apiService.updatePingolfSeasonCardPrice(season.id, Number(priceDraft));
      showToast(`Card buy-in is now ${formatMoney(updated.data.card_price_dollars)}`, 'success');
      setPriceDraft(null);
      onChanged();
    } catch (error) {
      showToast(error.response?.data?.error || 'Failed to update the card buy-in', 'error');
    } finally {
      setBusy(false);
    }
  };

  // Preview against a realistic field so the percentages read as money, not abstractions.
  const exampleField = 15;
  const examplePot = weeklyPerCard * exampleField;
  const draftWeekly = editing
    ? structures.weekly.find(entry => entry.key === form.weeklyPayoutPreset)
    : weeklyStructure;
  const draftSeasonSplit = editing
    ? structures.season.find(entry => entry.key === form.seasonPayoutStructure)?.split
    : season.season_pool_split;

  return (
    <section className="pl-admin-section">
      <div className="pl-admin-week-section-title">Buy-in and payouts</div>

      <div className="pl-admin-week-section">
        {isLocked && (
          <p className="pl-admin-field-help">
            {season.finalized_at
              ? 'This season is finalized, so its money settings are fixed.'
              : `${cardsSold} card${cardsSold === 1 ? ' has' : 's have'} been sold into this season, so the split and payout structures are fixed - they decide how money already paid in gets divided. The buy-in can still change, and applies to cards sold from now on.`}
          </p>
        )}

        {!editing ? (
          <>
            {priceDraft === null ? (
              <div className="pl-admin-readonly-row">
                <div className="pl-admin-readonly-field">
                  <span className="pl-admin-readonly-label">Card buy-in</span>
                  <span className="pl-admin-readonly-value">{formatMoney(cardPrice)}</span>
                </div>
                {!season.finalized_at && (
                  <DSButton
                    appearance="outline"
                    tone="neutral"
                    size="sm"
                    onClick={() => (isLocked ? setPriceDraft(String(cardPrice)) : startEditing())}
                    disabled={busy}
                  >
                    Edit
                  </DSButton>
                )}
              </div>
            ) : (
              <>
                <DSInput
                  label="Card buy-in ($)"
                  type="number"
                  min="0"
                  step="0.5"
                  value={priceDraft}
                  onChange={e => setPriceDraft(e.target.value)}
                  helpText={`What a player pays for one scorecard, from the next card sold. The season pool keeps taking ${formatMoney(seasonPerCard)} per card, leaving ${formatMoney(Math.max(Number(priceDraft) - seasonPerCard, 0))} for the weekly pot.`}
                />
                <div className="pl-admin-hole-actions">
                  <DSButton size="sm" onClick={savePrice} disabled={busy}>Save buy-in</DSButton>
                  <DSButton appearance="text" tone="neutral" size="sm" onClick={() => setPriceDraft(null)} disabled={busy}>
                    Cancel
                  </DSButton>
                </div>
              </>
            )}

            <p className="pl-admin-field-help">
              Every card splits {formatMoney(weeklyPerCard)} to that week&apos;s pot and{' '}
              {formatMoney(seasonPerCard)} to the season pool.
            </p>
          </>
        ) : (
          <div className="pl-admin-settings-fields">
            <DSInput
              label="Card buy-in ($)"
              type="number"
              min="0"
              step="0.5"
              value={form.cardPriceDollars}
              onChange={e => setForm({ ...form, cardPriceDollars: e.target.value })}
              helpText="What a player pays for one scorecard."
            />
            <DSInput
              label="To the season pool, per card ($)"
              type="number"
              min="0"
              step="0.5"
              value={form.seasonPoolPerCard}
              onChange={e => setForm({ ...form, seasonPoolPerCard: e.target.value })}
              helpText={`The rest goes to that week's pot - currently ${formatMoney(
                Math.max(Number(form.cardPriceDollars) - Number(form.seasonPoolPerCard), 0)
              )} per card.`}
            />
            <DSInput
              label="Weekly break-even floor ($)"
              type="number"
              min="0"
              step="1"
              value={form.weeklyFloorDollars}
              onChange={e => setForm({ ...form, weeklyFloorDollars: e.target.value })}
              helpText="Each paid place gets this back before the percentages apply, so a light night never costs a winner money. Set 0 to always split by percentage."
            />
            <DSSelect
              label="Weekly payout structure"
              value={form.weeklyPayoutPreset}
              onChange={e => setForm({ ...form, weeklyPayoutPreset: e.target.value })}
              helpText="How each week's pot divides across the night's net board."
            >
              {structures.weekly.map(entry => (
                <option key={entry.key} value={entry.key}>{entry.label}</option>
              ))}
            </DSSelect>
            <DSSelect
              label="Season payout structure"
              value={form.seasonPayoutStructure}
              onChange={e => setForm({ ...form, seasonPayoutStructure: e.target.value })}
              helpText="How the season pool divides across the final standings."
            >
              {/* A season whose split was set outside this screen matches no structure, so the
                  select would otherwise silently show the first option as if it were current. */}
              {!form.seasonPayoutStructure && <option value="">Custom - choose a structure</option>}
              {structures.season.map(entry => (
                <option key={entry.key} value={entry.key}>{entry.label}</option>
              ))}
            </DSSelect>
          </div>
        )}
      </div>

      <div className="pl-admin-week-section">
        <div className="pl-admin-payout-columns">
          <div className="pl-admin-payout-block">
            <h4>Weekly pot</h4>
            {draftWeekly ? (
              <ul className="pl-admin-payout-list">
                {draftWeekly.shares.map((share, index) => (
                  <li key={PLACE_LABELS[index] || index}>
                    <span>{PLACE_LABELS[index] || `Place ${index + 1}`}</span>
                    <span>{percent(share)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="pl-admin-field-help">This season uses a structure that is no longer offered.</p>
            )}
          </div>

          <div className="pl-admin-payout-block">
            <h4>Season pool</h4>
            {orderedSeasonSplit(draftSeasonSplit).length > 0 ? (
              <ul className="pl-admin-payout-list">
                {orderedSeasonSplit(draftSeasonSplit).map(row => (
                  <li key={row.key}>
                    <span>{row.label}</span>
                    <span>{percent(row.share)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="pl-admin-field-help">No season payout structure set.</p>
            )}
          </div>
        </div>

        {draftWeekly && examplePot > 0 && (
          <p className="pl-admin-field-help">
            On a {exampleField}-card week the pot is {formatMoney(examplePot)}, paying{' '}
            {draftWeekly.shares.map(share => formatMoney(Math.floor(examplePot * share))).join(' / ')}{' '}
            before the floor applies.
          </p>
        )}

        {editing && (
          <div className="pl-admin-hole-actions">
            <DSButton size="sm" onClick={save} disabled={busy}>Save settings</DSButton>
            <DSButton appearance="text" tone="neutral" size="sm" onClick={() => setEditing(false)} disabled={busy}>
              Cancel
            </DSButton>
          </div>
        )}
      </div>
    </section>
  );
}

export default PinGolfSeasonSettings;
